import React, { useEffect, lazy, Suspense } from "react";
import { Text } from "react-native";
import { ScrollView, StyleSheet, View } from "react-native";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { COLORS } from "../../constants";
import { useFilterView, FilterViewCommon } from "./FilterViewCommon";
import { getData } from "../../utils/storage";
import { logAnalyticsEvent } from "../../utils/firebase";
const FilterFooter = lazy(() => import("./FilterFooter"));

/**
 * FilterView component renders the filtering interface where users can select various attributes for filtering results.
 *
 * The component utilizes the `useFilterView` custom hook to manage the filter state, including which attributes are expanded,
 * the selected values, and how to handle user interactions with the filter options.
 *
 * The `FilterViewCommon` component is used to display the filterable attributes and their distinct values.
 * A reset button is provided to clear all filters, while the filter button applies the current filter selections.
 *
 * @returns {JSX.Element} A scrollable view that displays the filter attributes and buttons for resetting or applying filters.
 */
const FilterView = () => {
  logAnalyticsEvent("screen_view", { name: "Trails-FilterView" });
  const {
    attributes,
    expandedAttributes,
    selectedValues,
    handlePress,
    handleReset,
    handleItemPress,
    renderDistinctValues,
    setSelectedValues,
    handleFilterResults,
  } = useFilterView();

  // onload set the selected values from storage
  useEffect(() => {
    async function fetchData() {
      const storedValues = JSON.parse(await getData("filterValues"));
      if (storedValues) {
        setSelectedValues(storedValues);
      }
    }

    fetchData();
  }, []);

  return (
    <View style={styles.container}>
      <Suspense fallback={<Text>Loading...</Text>}>
        <ScrollView contentContainerStyle={styles.scrollContainer}>
          <FilterViewCommon
            attributes={attributes}
            expandedAttributes={expandedAttributes}
            selectedValues={selectedValues}
            handlePress={handlePress}
            handleItemPress={handleItemPress}
            renderDistinctValues={renderDistinctValues}
            FontAwesomeIcon={FontAwesomeIcon}
            iconSize={"lg"}
          />
        </ScrollView>
        <FilterFooter onReset={handleReset} onFilterResults={handleFilterResults} />
      </Suspense>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: COLORS.backgroundColor,
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
  },
  scrollContainer: {
    flexGrow: 1,
    justifyContent: "flex-start",
    backgroundColor: COLORS.backgroundColor,
    paddingBottom: 80,
  },
});

export default FilterView;
